import { supabase } from '../lib/supabase';
import { DEFAULT_TENANT_ID } from '../lib/defaultTenant';
import { EmailAutomationService } from './emailAutomationService';
import { supabaseEmailTemplateStorage } from './supabaseStorage';
import { resolveTemplateForLanguage } from './emailTemplateResolver';
import { EmailTemplate } from '../types/crm';
import { courseEmailContentService } from './courseEmailContentService';

type TriggerType =
  | 'booking_created'
  | 'booking_updated'
  | 'session_created'
  | 'session_updated'
  | 'session_cancelled'
  | 'session_reminder_7d'
  | 'session_reminder_24h'
  | 'certificate_ready';

type Recipient = {
  email: string;
  name?: string;
  customerId?: string;
  preferredLanguage?: string;
  placeholders?: Record<string, string>;
};

export type AutomationEvent = {
  trigger: TriggerType;
  entityId: string;
  subjectContext?: string;
  deliveryMode?: string | null;
  recipients: Recipient[];
  placeholders?: Record<string, string>;
  relatedBookingId?: string;
  relatedReminderId?: string;
  scheduledFor?: string;
};

type AutomationConfig = {
  enabled: boolean;
  templateId?: string;
  delayMinutes: number;
};

function applyPlaceholderMap(
  template: string,
  placeholders: Record<string, string>,
  skipKeys: string[] = [],
): string {
  let out = template;
  Object.entries(placeholders).forEach(([key, val]) => {
    if (skipKeys.includes(key)) return;
    const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    out = out.replace(regex, val || '');
  });
  return out;
}

function replacePlaceholders(template: string, placeholders: Record<string, string>): string {
  const first = applyPlaceholderMap(template, placeholders);
  // Nested tokens inside {{CourseEmailContent}} are resolved on a second pass.
  return applyPlaceholderMap(first, placeholders, ['{{CourseEmailContent}}']);
}

function parseBool(v?: string): boolean {
  return (v || '').toLowerCase() === 'true';
}

function parseNum(v?: string, fallback = 15): number {
  const n = Number(v);
  if (Number.isFinite(n) && n >= 0) return n;
  return fallback;
}

function dedupeKey(trigger: TriggerType, entityId: string, recipientEmail: string, language: string): string {
  return `${trigger}:${entityId}:${recipientEmail.toLowerCase()}:${language.toLowerCase()}`;
}

async function getConfig(trigger: TriggerType): Promise<AutomationConfig> {
  const { data } = await supabase
    .from('email_automation_settings')
    .select('setting_key, setting_value')
    .in('setting_key', [
      `automation.${trigger}.enabled`,
      `automation.${trigger}.template_id`,
      `automation.${trigger}.delay_minutes`,
      'automation.default_delay_minutes',
    ]);

  const map = new Map<string, string>();
  (data || []).forEach((row: any) => map.set(row.setting_key, row.setting_value || ''));

  const enabledRaw = map.get(`automation.${trigger}.enabled`);
  const defaultDelay = parseNum(map.get('automation.default_delay_minutes'), 15);
  const delay = parseNum(map.get(`automation.${trigger}.delay_minutes`), defaultDelay);
  return {
    enabled: enabledRaw == null ? true : parseBool(enabledRaw),
    templateId: map.get(`automation.${trigger}.template_id`) || undefined,
    delayMinutes: delay,
  };
}

async function existsByDedupe(dedupe: string): Promise<boolean> {
  const { data } = await supabase
    .from('email_outbox')
    .select('id, metadata')
    .in('status', ['pending', 'sent'])
    .limit(200);
  return (data || []).some((row: any) => row?.metadata?.dedupe_key === dedupe);
}

function applyDelay(baseIso: string | undefined, delayMinutes: number): string {
  const base = baseIso ? new Date(baseIso) : new Date();
  base.setMinutes(base.getMinutes() + Math.max(0, delayMinutes));
  return base.toISOString();
}

export class EmailAutomationOrchestrator {
  private static translateCache = new Map<string, { subject: string; body: string }>();

  static async processEvent(event: AutomationEvent): Promise<{ queued: number; skipped: number }> {
    const cfg = await getConfig(event.trigger);
    if (!cfg.enabled) return { queued: 0, skipped: event.recipients.length };

    const templates = await supabaseEmailTemplateStorage.getEmailTemplates();
    if (!templates.length) return { queued: 0, skipped: event.recipients.length };

    const baseTemplate =
      (cfg.templateId ? templates.find((t) => t.id === cfg.templateId) : undefined) ||
      this.pickDefaultTemplate(event.trigger, templates);
    if (!baseTemplate) return { queued: 0, skipped: event.recipients.length };

    let courseEmailContent = '';
    if (event.subjectContext?.trim()) {
      try {
        courseEmailContent = await courseEmailContentService.getCourseEmailContent(
          event.subjectContext,
          event.deliveryMode,
        );
      } catch (err) {
        console.warn('Failed to load course email content (non-blocking):', err);
      }
    }
    const eventPlaceholders = {
      ...(event.placeholders || {}),
      '{{CourseEmailContent}}': courseEmailContent,
    };

    let queued = 0;
    let skipped = 0;
    for (const recipient of event.recipients) {
      if (!recipient.email?.trim()) {
        skipped += 1;
        continue;
      }

      const resolved = await resolveTemplateForLanguage(
        templates,
        baseTemplate,
        recipient.preferredLanguage,
        this.translateCache
      );
      const langForDedupe = resolved.languageUsed || 'default';
      const dedupe = dedupeKey(event.trigger, event.entityId, recipient.email, langForDedupe);
      if (await existsByDedupe(dedupe)) {
        skipped += 1;
        continue;
      }

      const mergedPlaceholders = {
        ...eventPlaceholders,
        ...(recipient.placeholders || {}),
      };
      const subject = replacePlaceholders(resolved.template.subject || '', mergedPlaceholders);
      const body = replacePlaceholders(resolved.template.body || '', mergedPlaceholders);

      await EmailAutomationService.queueEmail({
        recipient_email: recipient.email,
        recipient_name: recipient.name,
        subject,
        html_content: body,
        text_content: body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
        email_type: `auto_${event.trigger}`,
        related_booking_id: event.relatedBookingId,
        related_customer_id: recipient.customerId,
        related_reminder_id: event.relatedReminderId,
        scheduled_for: applyDelay(event.scheduledFor, cfg.delayMinutes),
        metadata: {
          trigger_type: event.trigger,
          entity_id: event.entityId,
          recipient_email: recipient.email,
          language: langForDedupe,
          translated_from_base: resolved.translated,
          dedupe_key: dedupe,
          tenant_id: DEFAULT_TENANT_ID,
          subject_context: event.subjectContext || null,
        },
      });
      queued += 1;
    }

    return { queued, skipped };
  }

  private static pickDefaultTemplate(trigger: TriggerType, templates: EmailTemplate[]): EmailTemplate | undefined {
    const byName = (namePart: string) =>
      templates.find((t) => t.name.toLowerCase().includes(namePart.toLowerCase()));
    switch (trigger) {
      case 'booking_created':
      case 'booking_updated':
      case 'session_created':
      case 'session_updated':
      case 'session_reminder_7d':
      case 'session_reminder_24h':
        return byName('booking confirmation') || templates[0];
      case 'session_cancelled':
        return (
          byName('session cancelled') ||
          byName('course cancelled') ||
          byName('cancellation') ||
          byName('booking confirmation') ||
          templates[0]
        );
      case 'certificate_ready':
        return byName('certificate') || byName('reminder') || templates[0];
      default:
        return templates[0];
    }
  }
}
